import React, { useEffect, useMemo, useState } from 'react';
import { useAppearance } from '../../../../context/AppearanceContext';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const flattenCohorts = (cohorts, expandedIds, depth = 0) =>
  (cohorts || []).flatMap(cohort => {
    const children = Array.isArray(cohort.children) ? cohort.children : [];
    const row = [{ cohort, depth, hasChildren: children.length > 0 }];
    if (!expandedIds.has(cohort.id)) {
      return row;
    }
    return row.concat(flattenCohorts(children, expandedIds, depth + 1));
  });

const CohortListView = ({
  cohorts,
  activeCohortId,
  isLoading,
  isBusy,
  onToggleExpanded,
  onOpenCohort,
  onCreateCohort,
  onDuplicateCohort,
  onDeleteCohort,
}) => {
  const { formatDate } = useAppearance();
  const [expandedIds, setExpandedIds] = useState(new Set());

  useEffect(() => {
    setExpandedIds(new Set((cohorts || []).map(cohort => cohort.id)));
  }, [cohorts]);

  const rows = useMemo(
    () => flattenCohorts(cohorts, expandedIds),
    [cohorts, expandedIds],
  );

  const handleToggle = (cohortId) => {
    setExpandedIds(current => {
      const next = new Set(current);
      if (next.has(cohortId)) {
        next.delete(cohortId);
      } else {
        next.add(cohortId);
      }
      return next;
    });
    onToggleExpanded?.(cohortId);
  };

  if (isLoading) {
    return (
      <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', p: 4 }}>
        <Stack spacing={1.5} alignItems="center">
          <CircularProgress size={30} />
          <Typography variant="body2" color="text.secondary">
            Loading saved cohorts from the database...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          No saved cohorts yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 520 }}>
          Create a cohort from the list view, then open it in graph view to edit the hierarchy there.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={onCreateCohort}
          disabled={isBusy}
        >
          Create Cohort
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 1.25 }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Saved Cohorts
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Start from this overview list, then open a cohort in graph view to edit it.
          </Typography>
        </Box>

        <Button
          size="small"
          variant="outlined"
          startIcon={<AddCircleOutlineIcon />}
          onClick={onCreateCohort}
          disabled={isBusy}
        >
          New Cohort
        </Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Saved cohort list table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '14%' }}>ID</TableCell>
              <TableCell sx={{ width: '38%' }}>Name</TableCell>
              <TableCell sx={{ width: '16%' }}>Status</TableCell>
              <TableCell sx={{ width: '18%' }}>Updated</TableCell>
              <TableCell align="right" sx={{ width: '14%' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map(({ cohort, depth, hasChildren }) => {
              const isActive = activeCohortId === cohort.id;

              return (
                <TableRow
                  key={cohort.id}
                  hover
                  selected={isActive}
                  onDoubleClick={() => onOpenCohort(cohort.id)}
                  sx={{
                    cursor: 'pointer',
                    '& .cohort-name-link': {
                      color: 'inherit',
                    },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {cohort.id}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pl: depth * 2 }}>
                      <IconButton
                        size="small"
                        disabled={!hasChildren}
                        onClick={() => hasChildren && handleToggle(cohort.id)}
                        aria-label={hasChildren ? `Toggle ${cohort.name}` : `No children for ${cohort.name}`}
                      >
                        {hasChildren && expandedIds.has(cohort.id) ? (
                          <ExpandMoreIcon fontSize="small" />
                        ) : (
                          <ChevronRightIcon fontSize="small" sx={{ opacity: hasChildren ? 1 : 0.2 }} />
                        )}
                      </IconButton>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700 }}
                          noWrap
                          title={`${cohort.name} (double-click row to open in graph view)`}
                          className="cohort-name-link"
                        >
                          {cohort.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap title={cohort.description || ''}>
                          {cohort.description || 'Open in graph view to edit details'}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">{cohort.status || 'unedited'}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">{cohort.updatedAt ? formatDate(cohort.updatedAt, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                      <Tooltip title="Open in graph view">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onOpenCohort(cohort.id)}
                            disabled={isBusy}
                            aria-label={`Open ${cohort.name} in graph view`}
                          >
                            <AccountTreeIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Duplicate cohort">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onDuplicateCohort(cohort.id)}
                            disabled={isBusy}
                            aria-label={`Duplicate ${cohort.name}`}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Delete cohort">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onDeleteCohort(cohort.id)}
                            disabled={isBusy}
                            aria-label={`Delete ${cohort.name}`}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CohortListView;
